import React, {
  createContext,
  forwardRef,
  useMemo,
  useCallback,
  useState,
  useContext,
} from 'react'
import { View } from 'react-native'

import {
  Dialog as TDialog,
  withStaticProperties,
  DialogProps,
  styled,
  TamaguiElement,
  Button,
  Unspaced,
  getToken,
  ScrollView,
  useSheet,
  useSheetController,
  YStack,
} from 'tamagui'
import { H2, Paragraph } from '../Text'

import DismissIcon from '../Icon/icons/DismissIcon'

import './Dialog.css'

export const DIALOG_SHEET_SNAP_POINTS = [85, 50, 35]

const OriginalPortal = TDialog.Portal
const OriginalOverlay = TDialog.Overlay

export const DialogContext = createContext<{
  open: boolean | undefined
  setOpen: (newOpen: boolean) => void
}>({
  open: undefined,
  setOpen: () => undefined,
})

const DDialog = forwardRef<{ open: (val: boolean) => void }, DialogProps>(
  (props: DialogProps, ref) => {
    const [isOpen, setIsOpen] = useState(props.open || false)

    const onOpenChange = useCallback(
      (open: boolean) => {
        // console.log('ON OPEN CHANGE', open)
        // console.trace()

        if (props.onOpenChange) {
          props.onOpenChange(open)
        }

        setIsOpen(open)
      },
      [props.onOpenChange, setIsOpen]
    )

    const contextValue = useMemo(() => {
      return {
        open: isOpen,
        setOpen: setIsOpen,
      }
    }, [isOpen, setIsOpen])

    return (
      <DialogContext.Provider value={contextValue}>
        <TDialog
          key='dialog'
          modal
          {...props}
          onOpenChange={onOpenChange}
          ref={ref}
        >
          {props.children}
        </TDialog>
      </DialogContext.Provider>
    )
  }
)

const DialogPortal = React.forwardRef(
  ({ children, ...props }: { children: React.ReactNode }, ref) => {
    const { open } = useContext(DialogContext)

    const sheetController = useSheetController()

    // Body class management (dialog-blur-active) lives in DialogWrapper,
    // which is the sole owner for both desktop and sheet modes.
    // Managing it here caused a race condition: on mobile, the portal
    // initially mounts with isDesktopActive=true, then transitions to
    // false when the sheet adapter kicks in. The cleanup from the first
    // effect run would remove the class that DialogWrapper had added.

    return (
      <OriginalPortal
        key='portal'
        {...props}
        className={
          open ? 'new_Dialog new_Dialog_enter' : 'new_Dialog new_Dialog_exit'
        }
        display={sheetController.isHidden ? 'flex' : 'none'}
        paddingTop={60}
        ref={ref as React.Ref<TamaguiElement>}
      >
        <OriginalOverlay
          key='overlay'
          animation='fast'
          backgroundColor='$shadowColor'
          style={{ opacity: 0.2 }}
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
        />

        {children}
      </OriginalPortal>
    )
  }
)

export const DIALOG_BLUR_CLASS = 'dialog-blur-active'

// NOTE: DialogSheet is NOT mounted as a normal React component.
// Tamagui's Dialog.Adapt extracts its props as configuration and
// passes them to the internal sheet implementation. Hooks (useState,
// useEffect, useContext) defined here will NEVER run.
// Position tracking and body class management live in DialogWrapper.
const DialogSheet = React.forwardRef(
  ({ children, ...props }: { children: React.ReactNode }, ref) => {
    return (
      <TDialog.Sheet
        key='sheet'
        {...props}
        ref={ref as React.Ref<View>}
        zIndex={100_000}
        animation='fastSpring'
        modal
        snapPoints={DIALOG_SHEET_SNAP_POINTS}
        snapPointsMode='percent'
        dismissOnSnapToBottom
        dismissOnOverlayPress
      >
        <TDialog.Sheet.Overlay
          key='overlay'
          animation='medium'
          backgroundColor='$shadowColor'
          opacity={0.2}
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
        />

        {children}
      </TDialog.Sheet>
    )
  }
)

const StyledDialogContent = styled(TDialog.Content, {
  acceptsClassName: true,

  borderRadius: '$6',
  padding: 0,
  backgroundColor: '$backgroundSoft',

  animation: 'mediumSpring',
  x: 0,
  y: 0,
  rotateX: '0deg',

  opacity: 1,
  scale: 1,
  enterStyle: {
    x: 0,
    y: -100,
    rotateX: '-45deg',
    opacity: 0,
    scale: 1.2,
  },
  exitStyle: {
    x: 0,
    y: 50,
    opacity: 0,
    scale: 0.9,
    rotateX: '-20deg',
  },

  style: {
    boxShadow:
      'rgba(27, 19, 64, 0.5) 0px 4px 8px -4px, rgba(27, 19, 64, 0.3) 0px 5px 26px -7px',
    // transform: `translateZ(0)`,
    willChange: 'transform',
  },

  maxWidth: 600,
  width: '100%',

  maxHeight:
    'calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 80px)',

  // shadowRadius: '$2',
  // shadowColor: '$grey6',
})

const StyledCloseButton = styled(DismissIcon, {
  name: 'DialogCloseButton',

  // shadowColor: 'rgba(10, 37, 64, 0.18)',
  // shadowRadius: '$2',
  // shadowOffset: { width: 0, height: 2 },

  acceptsClassName: true,

  backgroundColor: '$backgroundSoft',
  color: '$color',
  borderWidth: 0,
  width: '$5',
  height: '$5',
  position: 'absolute',
  top: -4,
  right: -4,
  padding: '$1',
  // @ts-expect-error - borderRadius '50%' is a web-only value
  borderRadius: '50%',
  cursor: 'pointer',
  // animation: 'medium',
  // animateOnly: ['background-color'],

  hoverStyle: {
    backgroundColor: '$backgroundStrong',
  },
  // style: {
  //   boxShadow:
  //     'rgba(10, 37, 64, 0.12) 0px 4px 8px -4px, rgba(10, 37, 64, 0.09) 0px 5px 26px -7px',
  // },
})

export const Dialog = withStaticProperties(DDialog, {
  Portal: DialogPortal,
  Trigger: TDialog.Trigger,
  Content: StyledDialogContent.styleable(({ children, ...props }, ref) => {
    const sheetController = useSheetController()

    return (
      <StyledDialogContent
        key='content'
        {...props}
        ref={ref}
        onInteractOutside={(event) => {
          // Taken from: https://github.com/radix-vue/shadcn-vue/issues/107#issuecomment-1978420200
          const target = event.target as HTMLElement

          const isGrammarly =
            target && target.hasAttribute('data-grammarly-shadow-root')

          const is1Password = target.tagName === 'COM-1PASSWORD-BUTTON'

          if (isGrammarly || is1Password) {
            event.preventDefault()
          }
        }}
      >
        {sheetController.isHidden ? (
          <YStack
            flex={props.flex}
            gap={props.gap}
            padding={props.padding ?? '$5'}
            borderRadius='$6'
            style={{ overflowY: 'auto' }}
            minHeight='100%'
            maxHeight='100%'
            minWidth='100%'
            alignItems={props.alignItems}
          >
            {children}
          </YStack>
        ) : (
          children
        )}

        <Unspaced>
          <TDialog.Close asChild>
            <StyledCloseButton size={4} />
          </TDialog.Close>
        </Unspaced>
      </StyledDialogContent>
    )
  }),

  Title: styled(TDialog.Title, {
    name: 'NewDialogTitle',
    tag: 'h2',
    role: 'heading',
    fontWeight: '$6',
    size: '$6',
    style: {
      WebkitFontSmoothing: 'antialiased',
    },
  } as const),
  Description: styled(TDialog.Description, {
    tag: 'p',
    name: 'NewDialogDescription',
    fontWeight: '$4',

    variants: {
      size: {
        '...size': (size) => {
          return {
            fontSize: size,
            lineHeight: size,
          }
        },
      },
      marginAfter: {
        '...': (lineHeight, config) => {
          return {
            marginBottom:
              ((config.font?.lineHeight ?? {}) as Record<string, { variable?: string }>)[lineHeight]?.variable ?? lineHeight,
          }
        },
      },
    } as const,

    defaultVariants: {
      size: '$4',
    },
  } as const),
  Close: TDialog.Close,
  Adapt: TDialog.Adapt,

  Sheet: withStaticProperties(DialogSheet, {
    Overlay: TDialog.Sheet.Overlay,
    Frame: styled(TDialog.Sheet.Frame, { backgroundColor: '$backgroundSoft' }),
    Handle: styled(TDialog.Sheet.Handle, { animation: 'medium' }),
    ScrollView: styled(TDialog.Sheet.ScrollView, {
      paddingBottom: 'calc(env(safe-area-inset-bottom, 20px) + 20px)',

      // Override React Native Web's touch-action: none to allow native
      // browser scrolling. Without this, the Tamagui Sheet's PanResponder
      // intercepts all vertical gestures and moves the sheet instead of
      // scrolling the content.
      style: {
        touchAction: 'pan-y',
      },
    }),
  }),
})
